import { yupResolver } from '@hookform/resolvers/yup';
import { Card, Flex } from '@mantine/core';
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { BankAccountModel } from 'api-hooks/bank-account';
import { AuthorizationRules, NavigationRoutes } from 'common/constants';
import { ApiError, ApiResult } from 'common/repositories/common.model';
import { useAuthorization } from 'hooks/use-authorization';
import useDestroyAction from 'hooks/use-destroy-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useNavigation from 'hooks/use-navigation';
import Input from 'mantine-components/elements';
import { HalfGridContainer } from 'mantine-components/container/grid-container';
import Button from 'mantine-components/widget/button';
import Text from 'mantine-components/widget/text';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { UniskaiFormHeaderRef } from 'templates/form/uniskai-form-header';
import UniskaiFormTemplate from 'templates/form/uniskai-form-template';

import { BankAccountFormSchema, BankAccountFormType } from './form-type';
import useBankAccountDefaultValues from '../hooks/use-bank-account-default-values';

interface BankAccountFormProps {
  query?: UseQueryResult<ApiResult<BankAccountModel>, ApiError>;
  onSubmit: (
    input: BankAccountFormType,
    form: UseFormReturn<BankAccountFormType, any>,
  ) => Promise<BankAccountModel>;
  saveMutation?: UseMutationResult;
}

export default function BankAccountForm(props: BankAccountFormProps) {
  const { t } = useTranslation();
  const bankAccount = props.query?.data?.data;
  const defaultValues = useBankAccountDefaultValues(bankAccount);
  const titleRef = React.useRef<UniskaiFormHeaderRef>(null);

  React.useEffect(() => {
    let title = t(`pathname:${NavigationRoutes.BankAccountsCreate}`);
    if (bankAccount) {
      title = t(`pathname:${NavigationRoutes.BankAccountsView}`, {
        extra: bankAccount.code,
      });
    }
    titleRef.current?.setTitle(title);
  }, [bankAccount, t]);

  const permission = useGetCrudPermission('bank-accounts');
  const { deleteMutate, onDelete } = useDestroyAction('bank-accounts');
  const { can } = useAuthorization();
  const { navigate } = useNavigation();

  return (
    <UniskaiFormTemplate
      formProps={{
        defaultEditable: !props.query,
        defaultValues,
        onSubmit: props.onSubmit as any,
        resolver: yupResolver(BankAccountFormSchema()),
      }}
      query={props.query}
      titleRef={titleRef}
      data={bankAccount}
      saveMutation={props.saveMutation}
      customs={[
        {
          type: 'edit',
          show: permission.update,
        },
        {
          type: 'delete',
          loading: deleteMutate.isLoading,
          onClick: () => onDelete(bankAccount?.id),
          show: permission.delete,
        },
      ]}
    >
      <HalfGridContainer>
        <Input
          type="text"
          name="code"
          label={t('common:bank')}
          placeholder={t('common:bank')}
          required
        />
        <Input
          type="text"
          name="name"
          label={t('common:name')}
          placeholder={t('common:name')}
          required
        />
        <Input
          type="text"
          name="accountNumber"
          label={t('common:account_number')}
          placeholder={t('common:account_number')}
          required
        />
        <Input
          type="textarea"
          name="description"
          label={t('common:description')}
          placeholder={t('common:description')}
          required={false}
        />
      </HalfGridContainer>
      {bankAccount && (
        <Card withBorder radius="md" mt={16}>
          <Flex justify="space-between" align="center">
            <Text variant="body2Semibold">{t('common:balance')}</Text>
            {can(AuthorizationRules.ReportsBankMutation) && (
              <Button
                variant="subtle"
                onClick={() => {
                  navigate(NavigationRoutes.BankMutationReport, {
                    filter: {
                      bank_account: bankAccount.id,
                    },
                  });
                }}
              >
                {t('common:view_mutation')}
              </Button>
            )}
          </Flex>
          <Input
            type="number"
            name="balance"
            placeholder={t('common:balance')}
            disabled
          />
        </Card>
      )}
    </UniskaiFormTemplate>
  );
}
